import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';

interface DashboardStats {
  overview: {
    totalTenants: number;
    activeTenants: number;
    trialTenants: number;
    expiredTenants: number;
    totalUsers: number;
    activeSubscriptions: number;
  };
  expiringTrials: {
    count: number;
    tenants: Array<{
      id: string;
      name: string;
      domain: string;
      trialEndDate: string;
    }>;
  };
  recentSignups: {
    count: number;
    tenants: Array<{
      id: string;
      name: string;
      domain: string;
      createdAt: string;
      licenseStatus: string;
    }>;
  };
}

export default function Dashboard({ adminUser }: any) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/dashboard/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysUntil = (dateString: string) => {
    const days = Math.ceil(
      (new Date(dateString).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return days;
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  if (loading) {
    return (
      <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ color: '#666' }}>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <>
      <Navigation adminUser={adminUser} />
      <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', background: '#f9fafb', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
          MSP Dashboard
        </h1>
        <p style={{ color: '#6b7280' }}>Welcome back, {adminUser?.displayName}</p>
      </div>

      {/* KPI Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' }}>
        <div style={cardStyle}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Total Tenants</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats?.overview.totalTenants || 0}</div>
          <div style={{ fontSize: '12px', color: '#10b981', marginTop: '8px' }}>
            {stats?.overview.activeTenants || 0} active, {stats?.overview.trialTenants || 0} trial
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Active Subscriptions</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats?.overview.activeSubscriptions || 0}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
            Paying customers
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Trial Tenants</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats?.overview.trialTenants || 0}</div>
          <div style={{ fontSize: '12px', color: stats?.expiringTrials.count ? '#f59e0b' : '#6b7280', marginTop: '8px' }}>
            {stats?.expiringTrials.count || 0} expiring soon
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>Total Users</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{stats?.overview.totalUsers || 0}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
            Across all tenants
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {/* Expiring Trials */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>Trials Expiring Soon</h2>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>{stats?.expiringTrials.count || 0} tenants</span>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {stats?.expiringTrials.tenants.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}>
                No trials expiring in the next 7 days
              </div>
            ) : (
              stats?.expiringTrials.tenants.map((tenant) => {
                const daysUntil = getDaysUntil(tenant.trialEndDate);
                return (
                  <div
                    key={tenant.id}
                    onClick={() => navigate(`/tenants/${tenant.id}`)}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>{tenant.name}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>{tenant.domain}</div>
                    <div style={{ fontSize: '12px', color: daysUntil <= 3 ? '#ef4444' : '#f59e0b', fontWeight: '500' }}>
                      Expires in {daysUntil} day{daysUntil !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Signups */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>Recent Signups</h2>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Last 30 days</span>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {stats?.recentSignups.tenants.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}>
                No recent signups
              </div>
            ) : (
              stats?.recentSignups.tenants.map((tenant) => (
                <div
                  key={tenant.id}
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                  style={{
                    padding: '12px',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>{tenant.name}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>{tenant.domain}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        background: tenant.licenseStatus === 'ACTIVE' ? '#d1fae5' : '#fef3c7',
                        color: tenant.licenseStatus === 'ACTIVE' ? '#065f46' : '#92400e',
                        display: 'inline-block',
                        marginBottom: '4px',
                      }}>
                        {tenant.licenseStatus}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>{formatDate(tenant.createdAt)}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
